/**
 * Bande de marqueurs, sous un graphique.
 *
 * Le trait sur le graphique porte un numéro, pas un libellé : cinq libellés
 * entiers en haut d'une courbe se chevauchent et deviennent illisibles. Cette
 * bande est l'autre moitié du dispositif — elle donne, en texte, ce que le
 * numéro désigne et ce que la métrique a fait autour de cette date.
 *
 * Quatre choses y sont dites de chaque marqueur — son numéro, son jour, son
 * libellé, son verdict — et la couleur n'en porte aucune à elle seule : la
 * flèche donne le sens, le libellé le nomme en mots (« en hausse », « pas assez
 * de recul »), la couleur ne fait que le rappeler. C'est la règle de tout le
 * dashboard, et c'est ce qui la rend lisible en vision des couleurs déficiente.
 *
 * Absente quand il n'y a aucun marqueur sur la période : un graphique sans
 * annotation doit rester exactement ce qu'il était.
 */

import type { ChartMarkerItem } from '../hooks/useChartMarkers';
import { formatDayLong, formatDayShort } from '../lib/format';
import { verdictGlyph } from '../lib/markers';
import { IMPACT_TONE_COLORS } from '../lib/theme';

export function MarkerStrip({ items }: { items: readonly ChartMarkerItem[] }) {
  if (items.length === 0) return null;

  return (
    <ul className="marker-strip">
      {items.map((item) => (
        <li className="marker-strip__item" key={item.marker.id}>
          {/* `title` et non une infobulle maison : le detail des deux fenetres
              est une precision, pas une information dont la lecture depend. */}
          <span className="marker-strip__num" aria-hidden="true">
            {item.position}
          </span>

          {/* La date est ECRITE, pas seulement en infobulle : c'est elle qui
              dit ou lire la courbe. Le `title` porte la version longue, avec
              l'annee — indispensable sur une periode de plus d'un an. */}
          <span className="marker-strip__date" title={formatDayLong(item.marker.date)}>
            {formatDayShort(item.marker.date)}
          </span>

          <span className="marker-strip__label">{item.marker.label}</span>

          <span
            className="marker-strip__verdict"
            style={{ color: IMPACT_TONE_COLORS[item.tone] }}
            title={item.detail}
          >
            <span aria-hidden="true">{verdictGlyph(item.impact.verdict)}</span>{' '}
            {item.delta}
            <span className="sr-only"> — {item.verdict}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
